import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import LoadTrackingPanel from '../components/LoadTrackingPanel';
import LoadDocumentsPanel from '../components/LoadDocumentsPanel';
import { unitLabel, unitSubLabel } from '../utils/unitLabels';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaTruck, FaTrailer, FaUser, FaCalendarAlt,
  FaCheckCircle, FaTimes, FaUpload, FaSpinner,
  FaClock, FaMapMarkerAlt, FaWeightHanging,
  FaEye, FaArrowLeft, FaTrash, FaEdit,
  FaPlus, FaSave, FaFileAlt, FaImage, FaFilePdf,
  FaPlay, FaStop, FaFlagCheckered, FaInfoCircle,
  FaWeight, FaRoad, FaArrowRight, FaCloudUploadAlt,
  FaTint, FaExternalLinkAlt, FaEyeSlash, FaClipboardList
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';
import LoadPermitsPanel from '../components/LoadPermitsPanel';

function SubbieLoads({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loads, setLoads] = useState([]);
  const [user, setUser] = useState({});
  const [subbieId, setSubbieId] = useState(null);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [showWeighbridgeModal, setShowWeighbridgeModal] = useState(false);
  const [showPODModal, setShowPODModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    ticket_number: '',
    weight: '',
    pod_number: '',
    receiver_name: '',
    pod_weight: '',
    image_file: null,
    image_preview: null
  });
  const fileInputRef = useRef(null);

  // Telematics Modal State
  const [showTelematicsModal, setShowTelematicsModal] = useState(false);
  const [telematicsLoad, setTelematicsLoad] = useState(null);
  const [telematicsData, setTelematicsData] = useState({
    link: '',
    username: '',
    password: ''
  });
  const [showTelematicsPassword, setShowTelematicsPassword] = useState(false);
  const [savingTelematics, setSavingTelematics] = useState(false);

  // Permits & Escorts Modal State
  const [showPermitsModal, setShowPermitsModal] = useState(false);
  const [permitsLoad, setPermitsLoad] = useState(null);

  // Load Details Modal State
  const [showLoadDetailsModal, setShowLoadDetailsModal] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    getSubbieId(userData);
  }, []);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchLoads(savedId);
        return;
      }

      const response = await fetch(`${API_URL}/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const id = data.subcontractors[0].id;
        setSubbieId(id);
        localStorage.setItem('subbieId', id);
        fetchLoads(id);
      } else {
        setLoading(false);
        toast.error('No subcontractor profile found');
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchLoads = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/fleet/allocations/${id}?status=pending,on_route_to_loading,onsite,loading,en_route,at_destination,delivered`);
      const data = await response.json();
      if (data.success) {
        const activeLoads = data.allocations?.filter(load => load.status !== 'completed') || [];
        setLoads(activeLoads);
      } else {
        setError(data.error || 'Failed to fetch loads');
      }
    } catch (err) {
      console.error('Error fetching loads:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-gray-100 text-gray-700',
      'on_route_to_loading': 'bg-indigo-100 text-indigo-700',
      'onsite': 'bg-blue-100 text-blue-700',
      'loading': 'bg-yellow-100 text-yellow-700',
      'en_route': 'bg-purple-100 text-purple-700',
      'at_destination': 'bg-orange-100 text-orange-700',
      'delivered': 'bg-green-100 text-green-700',
      'completed': 'bg-green-100 text-green-700'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': '⏳ Pending',
      'on_route_to_loading': '🚛 Route to Load',
      'onsite': '📍 On Site',
      'loading': '🔄 Loading',
      'en_route': '🚛 En Route',
      'at_destination': '🏁 At Destination',
      'delivered': '✅ Delivered',
      'completed': '✅ Completed'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': <FaClock className="text-gray-500" />,
      'on_route_to_loading': <FaRoad className="text-indigo-500" />,
      'onsite': <FaMapMarkerAlt className="text-blue-500" />,
      'loading': <FaTruck className="text-yellow-500" />,
      'en_route': <FaTruck className="text-purple-500" />,
      'at_destination': <FaFlagCheckered className="text-orange-500" />,
      'delivered': <FaCheckCircle className="text-green-500" />,
      'completed': <FaCheckCircle className="text-green-500" />
    };
    return icons[status] || <FaClock className="text-gray-500" />;
  };

  const getTypeLabel = (type) => {
    const labels = { minerals: '⛏️ Minerals', agri: '🌾 Agri', abnormal: '🚛 Abnormal', container: '📦 Container', general: '📋 General' };
    return labels[type] || type;
  };

  const handleUpdateStatus = async (unitId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unitId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Status updated to ${getStatusLabel(newStatus)}`);
        fetchLoads(subbieId);
      } else {
        toast.error(data.error || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file (JPEG, PNG, etc.)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      const previewUrl = URL.createObjectURL(file);
      setUploadData(prev => ({
        ...prev,
        image_file: file,
        image_preview: previewUrl
      }));
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await fetch(`${API_URL}/uploads`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        return data.url;
      } else {
        throw new Error(data.error || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      throw err;
    }
  };

  const handleWeighbridgeUpload = async (unitId) => {
    if (!uploadData.ticket_number || !uploadData.weight) {
      toast.error('Please enter ticket number and weight');
      return;
    }

    if (!uploadData.image_file) {
      toast.error('Please upload an image of the weighbridge ticket');
      return;
    }

    setUploading(true);
    try {
      let imageUrl = null;
      try {
        imageUrl = await uploadImage(uploadData.image_file);
      } catch (err) {
        toast.error('Failed to upload image: ' + err.message);
        setUploading(false);
        return;
      }

      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unitId}/weighbridge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weighbridge_ticket_number: uploadData.ticket_number,
          weighbridge_weight_mt: parseFloat(uploadData.weight),
          weighbridge_image_url: imageUrl
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Weighbridge uploaded! Status updated to En Route.');
        setShowWeighbridgeModal(false);
        setUploadData({ 
          ticket_number: '', 
          weight: '', 
          pod_number: '', 
          receiver_name: '', 
          pod_weight: '', 
          image_file: null, 
          image_preview: null 
        });
        fetchLoads(subbieId);
      } else {
        toast.error(data.error || 'Failed to upload');
      }
    } catch (err) {
      console.error('Error uploading weighbridge:', err);
      toast.error('Failed to upload weighbridge');
    } finally {
      setUploading(false);
    }
  };

  const handlePODUpload = async (unitId) => {
    if (!uploadData.pod_number || !uploadData.receiver_name || !uploadData.pod_weight) {
      toast.error('Please enter POD number, receiver name, and weight');
      return;
    }

    if (!uploadData.image_file) {
      toast.error('Please upload an image of the POD');
      return;
    }

    setUploading(true);
    try {
      let imageUrl = null;
      try {
        imageUrl = await uploadImage(uploadData.image_file);
      } catch (err) {
        toast.error('Failed to upload image: ' + err.message);
        setUploading(false);
        return;
      }

      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unitId}/pod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pod_number: uploadData.pod_number,
          pod_receiver_name: uploadData.receiver_name,
          pod_weight_mt: parseFloat(uploadData.pod_weight),
          pod_image_url: imageUrl
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ POD uploaded! Status updated to Delivered. Waiting for admin approval.');
        setShowPODModal(false);
        setUploadData({ 
          ticket_number: '', 
          weight: '', 
          pod_number: '', 
          receiver_name: '', 
          pod_weight: '', 
          image_file: null, 
          image_preview: null 
        });
        fetchLoads(subbieId);
      } else {
        toast.error(data.error || 'Failed to upload POD');
      }
    } catch (err) {
      console.error('Error uploading POD:', err);
      toast.error('Failed to upload POD');
    } finally {
      setUploading(false);
    }
  };

  const calculateUnitProgress = (status) => {
    const order = ['pending', 'on_route_to_loading', 'onsite', 'loading', 'en_route', 'at_destination', 'delivered', 'completed'];
    const idx = order.indexOf(status);
    if (idx === -1) return 0;
    if (status === 'delivered') return 99;
    if (status === 'completed') return 100;
    return Math.round((idx / (order.length - 2)) * 100);
  };

  const groupLoadsByJob = () => {
    const grouped = {};
    loads.forEach(load => {
      const jobId = load.job_id;
      if (!grouped[jobId]) {
        grouped[jobId] = {
          job: load.job || { job_number: 'Unknown Job' },
          units: []
        };
      }
      grouped[jobId].units.push(load);
    });
    return grouped;
  };

  const groupedLoads = groupLoadsByJob();

  const closeWeighbridgeModal = () => {
    setShowWeighbridgeModal(false);
    setUploadData(prev => ({ 
      ...prev, 
      image_file: null, 
      image_preview: null 
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closePODModal = () => {
    setShowPODModal(false);
    setUploadData(prev => ({ 
      ...prev, 
      image_file: null, 
      image_preview: null 
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Telematics handlers
  const handleAddTelematics = (unit) => {
    setTelematicsLoad(unit);
    setTelematicsData({
      link: unit.tracking_link || '',
      username: unit.tracking_username || '',
      password: unit.tracking_password || ''
    });
    setShowTelematicsModal(true);
  };

  const handleSaveTelematics = async () => {
    if (!telematicsData.link) {
      toast.error('Please enter a tracking URL');
      return;
    }

    setSavingTelematics(true);
    try {
      const response = await fetch(`${API_URL}/subbie/fleet-allocations/${telematicsLoad.id}/telematics`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tracking_link: telematicsData.link,
          tracking_username: telematicsData.username || null,
          tracking_password: telematicsData.password || null
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Tracking link added successfully!');
        setShowTelematicsModal(false);
        setTelematicsData({ link: '', username: '', password: '' });
        fetchLoads(subbieId);
      } else {
        toast.error(data.error || 'Failed to add tracking link');
      }
    } catch (err) {
      console.error('Error adding telematics link:', err);
      toast.error('Failed to add tracking link');
    } finally {
      setSavingTelematics(false);
    }
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="loads" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading loads...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  if (error) {
    return (
      <SubbieLayout activeTab="loads" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Loads</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={() => fetchLoads(subbieId)} 
            className="mt-4 px-4 py-2 rounded-lg text-white" 
            style={{ backgroundColor: '#00A86B' }}
          >
            Retry
          </button>
        </div>
      </SubbieLayout>
    );
  }

  return (
    <SubbieLayout activeTab="loads" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">
            🚛 Active Loads
          </h1>
          <p className="text-sm text-gray-500">
            {loads.length} active units across {Object.keys(groupedLoads).length} jobs
          </p>
        </div>
        <button
          onClick={() => navigate('/subbie-active-jobs')}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:border-green hover:text-green transition-colors"
          style={{ borderColor: '#E8ECEF', color: '#4A5568' }}
        >
          <FaArrowLeft size={14} /> Back to Jobs
        </button>
      </div>

      {loads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold text-navy">All Loads Complete!</h3>
          <p className="text-sm text-gray-500">
            All your loads have been completed and approved. Great work!
          </p>
          <Link to="/subbie-active-jobs" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            View Active Jobs
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedLoads).map(([jobId, group]) => {
            const job = group.job;
            
            return (
              <div key={jobId} className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
                {/* Job Header */}
                <div className="flex flex-wrap justify-between items-start mb-3 pb-3 border-b" style={{ borderColor: '#E8ECEF' }}>
                  <div>
                    <h3 className="text-lg font-bold text-navy">
                      {job.job_number || 'Unknown Job'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                      {job.quote_type && job.quote_type !== 'minerals' && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-700">{getTypeLabel(job.quote_type)}</span>
                      )}
                      {job.total_quantity_mt > 0 && <span className="font-medium text-navy">{job.total_quantity_mt} MT</span>}
                      {job.quote_type === 'container' && job.container_size && <span>📦 {job.container_count || ''} × {job.container_size}{job.container_type ? ` ${job.container_type}` : ''}{job.is_empty ? ' (empty)' : ' (loaded)'}</span>}
                      {job.combined_weight_t > 0 && <span>⚖️ {job.combined_weight_t} t</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-navy">Units</span>
                    <span className="text-sm font-bold text-green-600">{group.units.length}</span>
                  </div>
                </div>

                {/* Units */}
                <div className="space-y-3">
                  {group.units.map((unit, index) => {
                    const order = ['pending', 'on_route_to_loading', 'onsite', 'loading', 'en_route', 'at_destination', 'delivered', 'completed'];
                    const currentIdx = order.indexOf(unit.status);
                    const showWeighbridge = unit.status === 'loading' && !unit.weighbridge_image_url;
                    const showPOD = unit.status === 'at_destination' && !unit.pod_image_url;
                    const unitProgress = calculateUnitProgress(unit.status);
                    
                    const hasWeighbridge = unit.weighbridge_image_url && unit.weighbridge_weight_mt;
                    const hasPOD = unit.pod_image_url && unit.pod_weight_mt;
                    const isPendingApproval = unit.status === 'delivered' && unit.pod_status === 'pending';
                    
                    const truckSweat = unit.weighbridge_weight_mt && unit.pod_weight_mt 
                      ? (unit.weighbridge_weight_mt - unit.pod_weight_mt) 
                      : null;
                    const showTruckSweat = truckSweat !== null && truckSweat > 0;
                    
                    return (
                      <div key={unit.id} className="bg-gray-50 rounded-lg p-4 border" style={{ borderColor: '#E8ECEF' }}>
                        <div className="flex flex-wrap items-center gap-4 mb-3">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(unit.status)}
                            <span className={getStatusBadge(unit.status)}>{getStatusLabel(unit.status)}</span>
                          </div>
                          <span className="text-sm font-medium text-navy">
                            Unit {index + 1}
                          </span>
                          <span className="text-xs text-gray-500">
                            Arrival: {unit.arrival_date || 'N/A'}
                          </span>
                          {unit.weighbridge_weight_mt && (
                            <span className="text-xs text-green-600 font-medium">
                              ⚖️ {unit.weighbridge_weight_mt} MT
                            </span>
                          )}
                          {unit.pod_weight_mt && (
                            <span className="text-xs text-blue-600 font-medium">
                              📄 {unit.pod_weight_mt} MT
                            </span>
                          )}
                          {showTruckSweat && (
                            <span className="text-xs text-orange-600 font-medium flex items-center gap-1">
                              <FaTint size={10} /> {truckSweat.toFixed(2)} MT (Truck Sweat)
                            </span>
                          )}
                          {isPendingApproval && (
                            <span className="text-xs text-yellow-600 flex items-center gap-1">
                              <FaClock size={10} /> Pending Admin Approval
                            </span>
                          )}
                          {unit.pod_status === 'approved' && (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <FaCheckCircle size={10} /> Approved
                            </span>
                          )}
                          {unit.pod_status === 'rejected' && (
                            <span className="text-xs text-red-600 flex items-center gap-1">
                              <FaTimes size={10} /> Rejected
                            </span>
                          )}
                          {unit.tracking_link && (
                            <a 
                              href={unit.tracking_link.startsWith('http') ? unit.tracking_link : `https://${unit.tracking_link}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              <FaExternalLinkAlt size={10} /> Track
                            </a>
                          )}
                        </div>

                        {/* UNIT STATUS PROGRESS BAR */}
                        <div className="mb-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium text-gray-600">Unit Progress</span>
                            <span className="text-xs font-bold text-green-600">{unitProgress}%</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${unitProgress}%`, backgroundColor: unitProgress === 99 ? '#f59e0b' : '#00A86B' }}
                            />
                          </div>
                          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                            <span>⏳ Pending</span>
                            <span>🚛 Route to Load</span>
                            <span>📍 On Site</span>
                            <span>🔄 Loading</span>
                            <span>🚛 En Route</span>
                            <span>🏁 Destination</span>
                            <span>✅ Delivered</span>
                          </div>
                          {isPendingApproval && (
                            <p className="text-xs text-yellow-600 mt-1 text-center">
                              ⏳ Waiting for admin approval to complete
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <div>
                            <p className="text-xs text-gray-500">Tractor</p>
                            <p className="font-medium text-sm text-navy">{unitLabel(unit.truck) || 'N/A'}</p>
                            <p className="text-xs text-gray-400">{unitSubLabel(unit.truck)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Trailer 1</p>
                            <p className="font-medium text-sm text-navy">{unit.trailer_1?.registration_number || 'N/A'}</p>
                            <p className="text-xs text-gray-400">{unit.trailer_1?.trailer_type}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Trailer 2</p>
                            <p className="font-medium text-sm text-navy">{unit.trailer_2?.registration_number || 'N/A'}</p>
                            <p className="text-xs text-gray-400">{unit.trailer_2?.trailer_type}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Driver</p>
                            <p className="font-medium text-sm text-navy">{unit.driver?.full_name || 'N/A'}</p>
                            <p className="text-xs text-gray-400">{unit.driver?.phone}</p>
                          </div>
                        </div>

                        {/* SIMPLIFIED STATUS BUTTONS */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {order.map((status, idx) => {
                            if (status === 'completed') return null;
                            
                            const isActive = unit.status === status;
                            const isPast = idx < currentIdx;
                            const isNext = idx === currentIdx + 1;
                            
                            let bgColor = '#e5e7eb';
                            let textColor = '#374151';
                            let borderColor = '#d1d5db';
                            let hoverEffect = '';
                            let icon = null;
                            let fontWeight = '500';
                            let disabled = true;
                            let cursor = 'default';
                            let title = '';
                            let label = getStatusLabel(status);
                            
                            if (isActive) {
                              bgColor = '#059669';
                              textColor = '#ffffff';
                              borderColor = '#059669';
                              icon = <FaCheckCircle className="inline mr-1" size={10} />;
                              fontWeight = '600';
                              disabled = false;
                              cursor = 'pointer';
                              label = getStatusLabel(status);
                            }
                            else if (isPast) {
                              bgColor = '#d1fae5';
                              textColor = '#047857';
                              borderColor = '#a7f3d0';
                              icon = <FaCheckCircle className="inline mr-1" size={10} />;
                              fontWeight = '500';
                              disabled = false;
                              cursor = 'default';
                              label = getStatusLabel(status);
                            }
                            else if (isNext) {
                              bgColor = '#2563eb';
                              textColor = '#ffffff';
                              borderColor = '#2563eb';
                              hoverEffect = 'hover:bg-blue-700';
                              fontWeight = '600';
                              disabled = false;
                              cursor = 'pointer';
                              label = getStatusLabel(status) + ' →';
                              
                              if (status === 'at_destination') {
                                bgColor = '#2563eb';
                                textColor = '#ffffff';
                                borderColor = '#2563eb';
                                hoverEffect = 'hover:bg-blue-700';
                                disabled = false;
                                cursor = 'pointer';
                                label = '🏁 At Destination →';
                              }
                              
                              if (status === 'en_route' && !hasWeighbridge) {
                                bgColor = '#fef3c7';
                                textColor = '#92400e';
                                borderColor = '#f59e0b';
                                disabled = true;
                                cursor = 'default';
                                hoverEffect = '';
                                label = '⚠️ Upload Weighbridge First';
                                title = 'Upload weighbridge ticket first';
                              }
                              
                              if (status === 'delivered' && !hasPOD) {
                                bgColor = '#fef3c7';
                                textColor = '#92400e';
                                borderColor = '#f59e0b';
                                disabled = true;
                                cursor = 'default';
                                hoverEffect = '';
                                label = '⚠️ Upload POD First';
                                title = 'Upload POD first';
                              }
                            }
                            else {
                              bgColor = '#e5e7eb';
                              textColor = '#374151';
                              borderColor = '#d1d5db';
                              disabled = true;
                              cursor = 'default';
                              label = getStatusLabel(status);
                            }
                            
                            return (
                              <button
                                key={status}
                                onClick={() => isNext && !disabled && handleUpdateStatus(unit.id, status)}
                                disabled={disabled}
                                title={title}
                                style={{
                                  backgroundColor: bgColor,
                                  color: textColor,
                                  borderColor: borderColor,
                                  fontWeight: fontWeight,
                                  cursor: cursor,
                                  padding: '6px 12px',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  border: '1px solid',
                                  transition: 'all 0.2s'
                                }}
                                className={hoverEffect}
                              >
                                {icon}
                                {label}
                              </button>
                            );
                          })}
                        </div>

                        {/* Upload Buttons + Telematics */}
                        <div className="flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: '#E8ECEF' }}>
                          <button
                            onClick={() => { setSelectedLoad(unit); setShowLoadDetailsModal(true); }}
                            style={{ backgroundColor: '#4B5563', color: 'white', fontWeight: '500', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                          >
                            <FaEye size={12} /> View Details
                          </button>
                          {showWeighbridge && (
                            <button
                              onClick={() => { setSelectedLoad(unit); setShowWeighbridgeModal(true); }}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors flex items-center gap-1"
                            >
                              <FaWeight size={12} /> Upload Weighbridge Ticket
                            </button>
                          )}
                          {showPOD && (
                            <button
                              onClick={() => { setSelectedLoad(unit); setShowPODModal(true); }}
                              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs hover:bg-purple-700 transition-colors flex items-center gap-1"
                            >
                              <FaFileAlt size={12} /> Upload POD
                            </button>
                          )}
                          
                          {/* ✅ TELEMATICS LINK BUTTON */}
                          <button
                            onClick={() => handleAddTelematics(unit)}
                            style={{
                              backgroundColor: '#00A86B',
                              color: 'white',
                              fontWeight: '500',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                          >
                            <FaExternalLinkAlt size={12} /> {unit.tracking_link ? 'Update Tracking Link' : 'Add Tracking Link'}
                          </button>

                          {/* ✅ PERMITS & ESCORTS BUTTON */}
                          <button
                            onClick={() => { setPermitsLoad(unit); setShowPermitsModal(true); }}
                            style={{
                              backgroundColor: '#7c3aed',
                              color: 'white',
                              fontWeight: '500',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6D28D9'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                          >
                            <FaClipboardList size={12} /> Permits & Escorts
                          </button>

                          {unit.weighbridge_image_url && (
                            <span className="text-xs text-green-600 flex items-center gap-1">✅ Weighbridge uploaded ({unit.weighbridge_weight_mt} MT)</span>
                          )}
                          {unit.pod_image_url && unit.pod_status === 'pending' && (
                            <span className="text-xs text-yellow-600 flex items-center gap-1">⏳ POD pending approval</span>
                          )}
                          {unit.pod_image_url && unit.pod_status === 'approved' && (
                            <span className="text-xs text-green-600 flex items-center gap-1">✅ POD approved</span>
                          )}
                          {unit.pod_image_url && unit.pod_status === 'rejected' && (
                            <span className="text-xs text-red-600 flex items-center gap-1">❌ POD rejected</span>
                          )}
                          {unit.tracking_link && (
                            <span className="text-xs text-blue-600 flex items-center gap-1">
                              <FaExternalLinkAlt size={10} /> Tracking active
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Weighbridge Modal */}
      {showWeighbridgeModal && selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closeWeighbridgeModal}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-4">⚖️ Upload Weighbridge Ticket</h2>
            <p className="text-sm text-gray-500 mb-4">Unit: {selectedLoad.truck?.registration_number}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Number *</label>
                <input
                  type="text"
                  value={uploadData.ticket_number}
                  onChange={(e) => setUploadData({ ...uploadData, ticket_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="e.g. WB-2026-001"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (MT) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={uploadData.weight}
                  onChange={(e) => setUploadData({ ...uploadData, weight: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="e.g. 32.8"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image *</label>
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-green-500 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {uploadData.image_preview ? (
                    <div className="space-y-2">
                      <img 
                        src={uploadData.image_preview} 
                        alt="Preview" 
                        className="max-h-40 mx-auto rounded-lg object-contain"
                      />
                      <p className="text-sm text-green-600">✅ Image uploaded</p>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadData(prev => ({ ...prev, image_file: null, image_preview: null }));
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FaCloudUploadAlt className="text-4xl text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Click to upload image of weighbridge ticket</p>
                      <p className="text-xs text-gray-400">JPEG, PNG (max 10MB)</p>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">
                <FaInfoCircle className="inline mr-1" /> Uploading will automatically update status to <strong>En Route</strong>
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={closeWeighbridgeModal}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleWeighbridgeUpload(selectedLoad.id)}
                disabled={uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload />}
                {uploading ? 'Uploading...' : 'Upload & Set En Route'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* POD Modal */}
      {showPODModal && selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closePODModal}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-4">📄 Upload POD</h2>
            <p className="text-sm text-gray-500 mb-4">Unit: {selectedLoad.truck?.registration_number}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">POD Number *</label>
                <input
                  type="text"
                  value={uploadData.pod_number}
                  onChange={(e) => setUploadData({ ...uploadData, pod_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="e.g. POD-2026-001"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Name *</label>
                <input
                  type="text"
                  value={uploadData.receiver_name}
                  onChange={(e) => setUploadData({ ...uploadData, receiver_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="e.g. John Smith"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">POD Weight (MT) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={uploadData.pod_weight}
                  onChange={(e) => setUploadData({ ...uploadData, pod_weight: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="e.g. 32.8"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image *</label>
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {uploadData.image_preview ? (
                    <div className="space-y-2">
                      <img 
                        src={uploadData.image_preview} 
                        alt="Preview" 
                        className="max-h-40 mx-auto rounded-lg object-contain"
                      />
                      <p className="text-sm text-green-600">✅ Image uploaded</p>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadData(prev => ({ ...prev, image_file: null, image_preview: null }));
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FaCloudUploadAlt className="text-4xl text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Click to upload image of POD</p>
                      <p className="text-xs text-gray-400">JPEG, PNG (max 10MB)</p>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">
                <FaInfoCircle className="inline mr-1" /> Uploading will automatically update status to <strong>Delivered</strong> and notify admin for approval
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={closePODModal}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePODUpload(selectedLoad.id)}
                disabled={uploading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload />}
                {uploading ? 'Uploading...' : 'Upload & Deliver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TELEMATICS MODAL */}
      {showTelematicsModal && telematicsLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowTelematicsModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                <FaExternalLinkAlt className="text-green-500" /> {telematicsLoad.tracking_link ? 'Update' : 'Add'} Tracking Link
              </h2>
              <button onClick={() => setShowTelematicsModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Unit: {telematicsLoad.truck?.registration_number} • Job: {telematicsLoad.job?.job_number}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tracking URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={telematicsData.link}
                  onChange={(e) => setTelematicsData({ ...telematicsData, link: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="https://track.telematics.com/vehicle/12345"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Paste the full tracking URL provided by your telematics provider</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username (Optional)
                </label>
                <input
                  type="text"
                  value={telematicsData.username}
                  onChange={(e) => setTelematicsData({ ...telematicsData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="Enter username if required"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password (Optional)
                </label>
                <div className="relative">
                  <input
                    type={showTelematicsPassword ? 'text' : 'password'}
                    value={telematicsData.password}
                    onChange={(e) => setTelematicsData({ ...telematicsData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="Enter password if required"
                  />
                  <button
                    type="button"
                    onClick={() => setShowTelematicsPassword(!showTelematicsPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showTelematicsPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Password is stored encrypted and only visible to client/manager</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowTelematicsModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTelematics}
                disabled={savingTelematics || !telematicsData.link}
                className="px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {savingTelematics ? <FaSpinner className="animate-spin" /> : <FaSave size={14} />}
                {savingTelematics ? 'Saving...' : 'Save Tracking Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ LOAD DETAILS MODAL ═══ */}
      {showLoadDetailsModal && selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowLoadDetailsModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">📋 Load Details</h2>
              <button onClick={() => setShowLoadDetailsModal(false)} className="text-gray-500 hover:text-navy"><FaTimes size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><p className="text-xs text-gray-500">Job</p><p className="font-medium text-navy">{selectedLoad.job?.job_number || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Status</p><span className={getStatusBadge(selectedLoad.status)}>{getStatusLabel(selectedLoad.status)}</span></div>
              <div><p className="text-xs text-gray-500">Tractor</p><p className="font-medium text-navy">{selectedLoad.truck?.registration_number || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Driver</p><p className="font-medium text-navy">{selectedLoad.driver?.full_name || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Trailer 1</p><p className="font-medium text-navy">{selectedLoad.trailer_1?.registration_number || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Arrival</p><p className="font-medium text-navy">{selectedLoad.arrival_date || 'N/A'}</p></div>
            </div>

            {/* The subbie adds the provider tracking link here. Admin and subbie can add or replace
                it; the client only ever sees it. Expiry shows because share links lapse. */}
            <div className="mb-4">
              <LoadTrackingPanel
                load={selectedLoad}
                saveUrl={`${API_URL}/subbie/fleet-allocations/${selectedLoad.id}/telematics`}
                canEdit
                onSaved={(updated) => {
                  if (updated) setSelectedLoad((prev) => ({ ...prev, ...updated }));
                  // Refresh the underlying list as well: the modal opens from a copy of that row, so
                  // without this the link looked saved until the modal was reopened.
                  fetchLoads(subbieId);
                }}
              />
            </div>

            {/* Anything that belongs to this load: border permits, proceed-to-border letters, etc. */}
            <div className="mb-4">
              <LoadDocumentsPanel loadId={selectedLoad.id} />
            </div>

            {selectedLoad.job?.job_description && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-1">Job Description</p>
                <p className="text-sm text-gray-700 whitespace-pre-line">{selectedLoad.job.job_description}</p>
              </div>
            )}
            {(() => {
              const li = (Array.isArray(selectedLoad.job?.line_items) && selectedLoad.job.line_items.length)
                ? selectedLoad.job.line_items
                : (Array.isArray(selectedLoad.job?.items) && selectedLoad.job.items.length ? selectedLoad.job.items : []);
              return li.length > 0 ? (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">Line Items (your cost)</p>
                  <div className="space-y-2">
                    {li.map((x, i) => (
                      <div key={x.id || i} className="border rounded-lg p-2.5" style={{ borderColor: '#E8ECEF' }}>
                        <div className="flex justify-between gap-2">
                          <p className="text-sm font-medium text-navy">{x.title || x.description || x.commodity || 'Line item'}</p>
                          {(x.cost_price || x.rate || 0) > 0 && <p className="text-sm font-semibold text-green-700 flex-shrink-0">R{Number(x.cost_price || x.rate || 0).toFixed(2)}</p>}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {x.quantity || 1} × {x.unit || 'load'}
                          {(x.pickup_location || x.delivery_location) ? ` · ${x.pickup_location || '?'} → ${x.delivery_location || '?'}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}
            {selectedLoad.job?.notes && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700"><strong>Notes:</strong> {selectedLoad.job.notes}</div>
            )}
          </div>
        </div>
      )}

      {/* ═══ PERMITS & ESCORTS MODAL ═══ */}
      {showPermitsModal && permitsLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPermitsModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                <FaClipboardList className="text-purple-600" /> Permits & Escorts
              </h2>
              <button onClick={() => setShowPermitsModal(false)} className="text-gray-500 hover:text-navy"><FaTimes size={20} /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {permitsLoad.job?.job_number || 'Job'} · Tractor: {permitsLoad.truck?.registration_number || 'N/A'} · {permitsLoad.status}
            </p>
            <LoadPermitsPanel loadId={permitsLoad.id} />
          </div>
        </div>
      )}
    </SubbieLayout>
  );
}

export default SubbieLoads;
